import OfflineManuals from "./routes/OfflineManuals";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AppShell } from "./components/AppShell";
import { Loading, NotBuiltYet } from "./components/ui";
import { SyncStatus } from "./components/SyncStatus";
import { useAuth } from "./lib/auth";
import { OfflineProvider } from "./lib/offline";

import Catalogue from "./routes/Catalogue";
import Projects from "./routes/Projects";
import Login from "./routes/Login";
import CustomerDetail from "./routes/CustomerDetail";
import Customers from "./routes/Customers";
import Dispatch from "./routes/Dispatch";
import NewJob from "./routes/NewJob";
import NewQuote from "./routes/NewQuote";
import Jobs from "./routes/Jobs";
import JobDetail from "./routes/JobDetail";
import Quotes from "./routes/Quotes";
import QuoteDetail from "./routes/QuoteDetail";
import Invoices from "./routes/Invoices";
import InvoiceDetail from "./routes/InvoiceDetail";
import Sites from "./routes/Sites";
import SiteDetail from "./routes/SiteDetail";
import Field from "./routes/Field";
import Settings from "./routes/Settings";
import Stock from "./routes/Stock";
import Contracts from "./routes/Contracts";
import PublicQuote from "./routes/PublicQuote";
import ChangePassword from "./routes/ChangePassword";

/** Everything behind this needs a session. */
function Protected({ children }: { children: React.ReactNode }) {
  const { status, session } = useAuth();
  const location = useLocation();

  if (status === "checking") return <Loading what="Checking your session" />;
  if (status === "authenticated" && session?.mustChangePassword) return <ChangePassword />;
  if (status === "anonymous") {
    // Remember where they were going, so signing in returns them there.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      {/* The customer's quote. Outside `Protected` on purpose, and outside the
          shell: no nav, no sync engine, no sign-in redirect. A stranger opening
          this from an email must never be bounced to a login screen for a
          product they have never heard of. It sits above the catch-all below,
          which would otherwise swallow it. */}
      <Route path="/q/:token" element={<PublicQuote />} />
      <Route
        path="*"
        element={
          <Protected>
            {/* The engine runs for the whole session, not per screen: a tech
                who leaves the app on the job card still wants their queue
                draining when the van comes out of the tunnel. */}
            <OfflineProvider enabled>
            <SyncStatus />
            <AppShell>
              <Routes>
                <Route path="/" element={<Dispatch />} />
                <Route path="/jobs" element={<Jobs />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/catalogue" element={<Catalogue />} />
                <Route path="/jobs/new" element={<NewJob />} />
                <Route path="/jobs/:id" element={<JobDetail />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/customers/:id" element={<CustomerDetail />} />
                <Route path="/quotes" element={<Quotes />} />
                <Route path="/quotes/new" element={<NewQuote />} />
                <Route path="/quotes/:id" element={<QuoteDetail />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/invoices/:id" element={<InvoiceDetail />} />
                <Route path="/sites" element={<Sites />} />
                <Route path="/sites/:id" element={<SiteDetail />} />
                <Route path="/field" element={<Field />} />
                <Route path="/manuals" element={<OfflineManuals />} />
                <Route path="/settings" element={<Settings />} />

                <Route path="/stock" element={<Stock />} />
                <Route path="/contracts" element={<Contracts />} />

                {/* Routed, honestly empty. Each names the card that will fill it. */}
                <Route
                  path="/compliance"
                  element={
                    <NotBuiltYet
                      module="Compliance"
                      card="COMPLY-1"
                      what="AS/NZS 2201 test sheets that produce the certificate, roll each asset's next-due date, and block a job start when a licence or induction has lapsed."
                      depends="COMPLY-1 · the form engine"
                    />
                  }
                />
                <Route
                  path="/insight"
                  element={
                    <NotBuiltYet
                      module="Insight"
                      card="INSIGHT-1"
                      what="Revenue mix, margin by work type, WIP ageing, utilisation with apprentice hours separated, and the jobs losing money right now — the twelve reports a trade actually opens."
                      depends="INSIGHT-1 · reporting endpoints"
                    />
                  }
                />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </AppShell>
            </OfflineProvider>
          </Protected>
        }
      />
    </Routes>
  );
}

function NotFound() {
  return (
    <div className="page">
      <div className="notbuilt">
        <h2>No such page</h2>
        <p>That address doesn't match anything in Keystone.</p>
      </div>
    </div>
  );
}
